// src/pages/CronJobs.js
import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Plus, Edit2, Trash2, RefreshCw, Play, CheckCircle, AlertTriangle, Pause, X, Save, Search, FileText } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CFG = {
  active:   { label: 'Activ',    color: 'var(--success)', bg: '#22c55e20' },
  paused:   { label: 'Pauză',   color: 'var(--warning)', bg: '#f59e0b20' },
  disabled: { label: 'Dezactivat', color: 'var(--text-secondary)', bg: 'var(--bg-hover)' },
};

const LAST_STATUS_CFG = {
  success: { label: 'Success', color: 'var(--success)', icon: CheckCircle },
  failed:  { label: 'Eșuat',   color: 'var(--danger)',  icon: AlertTriangle },
  running: { label: 'Rulează', color: 'var(--accent)',  icon: Clock },
};

const CRON_EXAMPLES = [
  { label: 'La fiecare minut',   value: '* * * * *' },
  { label: 'La fiecare oră',     value: '0 * * * *' },
  { label: 'Zilnic 02:00',       value: '0 2 * * *' },
  { label: 'Zilnic 00:00',       value: '0 0 * * *' },
  { label: 'Săptămânal duminică', value: '0 0 * * 0' },
  { label: 'Lunar pe 1',         value: '0 0 1 * *' },
  { label: 'La 5 minute',        value: '*/5 * * * *' },
];

function JobModal({ job, onClose, onSaved }) {
  const isEdit = !!job?.id;
  const [f, setF] = useState(isEdit ? { ...job } : {
    name: '', description: '', schedule: '0 2 * * *', command: '', type: 'shell',
    target_host: '', timeout_seconds: 300, retry_on_fail: false, notify_on_fail: '', status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState(null);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const validate = async (expr) => {
    if (!expr) { setValidation(null); return; }
    try {
      const { data } = await api.post('/cron/validate', { expression: expr });
      setValidation(data);
    } catch { setValidation(null); }
  };

  const save = async () => {
    if (!f.name || !f.schedule || !f.command) return toast.error('Nume, schedule și comandă sunt obligatorii');
    setLoading(true);
    try {
      if (isEdit) { await api.put(`/cron/jobs/${job.id}`, f); toast.success('Job actualizat'); }
      else { await api.post('/cron/jobs', f); toast.success('Job creat'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <span className="modal-title"><Clock size={16} /> {isEdit ? job.name : 'Cron Job Nou'}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Nume *</label><input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Tip</label>
              <select className="form-select" value={f.type} onChange={e => set('type', e.target.value)}>
                <option value="shell">Shell / Bash</option>
                <option value="http">HTTP Request</option>
                <option value="docker">Docker Exec</option>
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Cron Schedule *</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                {CRON_EXAMPLES.map(ex => (
                  <button key={ex.value} type="button" onClick={() => { set('schedule', ex.value); validate(ex.value); }}
                    style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, background: f.schedule === ex.value ? 'var(--accent)' : 'var(--bg-hover)', color: f.schedule === ex.value ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                    {ex.label}
                  </button>
                ))}
              </div>
              <input className="form-input" value={f.schedule} onChange={e => { set('schedule', e.target.value); validate(e.target.value); }} placeholder="* * * * *" style={{ fontFamily: 'monospace' }} />
              {validation && (
                <div style={{ marginTop: 4, fontSize: 12, color: validation.valid ? 'var(--success)' : 'var(--danger)', display: 'flex', gap: 6 }}>
                  {validation.valid ? <CheckCircle size={13} /> : <AlertTriangle size={13} />}
                  {validation.description || validation.message}
                  {validation.next && <span style={{ color: 'var(--text-secondary)' }}>· Următoarea rulare: {new Date(validation.next).toLocaleString('ro-RO')}</span>}
                </div>
              )}
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Comandă *</label>
              <input className="form-input" style={{ fontFamily: 'monospace' }} value={f.command} onChange={e => set('command', e.target.value)}
                placeholder={f.type === 'http' ? 'https://api.example.com/webhook' : f.type === 'docker' ? 'php artisan schedule:run' : '/usr/bin/backup.sh'} />
            </div>
            {f.type === 'docker' && (
              <div className="form-group"><label className="form-label">Container ID/Nume</label><input className="form-input" value={f.target_host || ''} onChange={e => set('target_host', e.target.value)} /></div>
            )}
            <div className="form-group"><label className="form-label">Timeout (secunde)</label><input className="form-input" type="number" value={f.timeout_seconds} onChange={e => set('timeout_seconds', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Notificare la eșec (email)</label><input className="form-input" type="email" value={f.notify_on_fail || ''} onChange={e => set('notify_on_fail', e.target.value)} /></div>
            {isEdit && (
              <div className="form-group"><label className="form-label">Status</label>
                <select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}>
                  {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginTop: 8 }}>
            <input type="checkbox" checked={!!f.retry_on_fail} onChange={e => set('retry_on_fail', e.target.checked)} />
            Reîncearcă la eșec
          </label>
          <div className="form-group" style={{ marginTop: 12 }}><label className="form-label">Descriere</label><textarea className="form-textarea" value={f.description || ''} onChange={e => set('description', e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Creare'}</>}</button>
        </div>
      </div>
    </div>
  );
}

function RunsModal({ job, onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    api.get(`/cron/jobs/${job.id}/runs`)
      .then(r => setRuns(r.data))
      .catch(() => toast.error('Eroare'))
      .finally(() => setLoading(false));
  }, [job.id]);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header"><span className="modal-title"><FileText size={16} /> Rulări — {job.name}</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          {loading ? <div className="loading-center"><span className="spinner" /></div> : runs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Nicio rulare</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {runs.map(r => {
                const cfg = LAST_STATUS_CFG[r.status] || LAST_STATUS_CFG.failed;
                return (
                  <div key={r.id} style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px', border: `1px solid ${r.status === 'success' ? '#22c55e33' : r.status === 'failed' ? '#ef444433' : 'var(--border)'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: r.output ? 6 : 0 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <cfg.icon size={14} color={cfg.color} />
                        <span style={{ fontWeight: 600, fontSize: 13, color: cfg.color }}>{cfg.label}</span>
                        {r.duration && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.duration}s</span>}
                        {r.exit_code !== null && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>exit: {r.exit_code}</span>}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{new Date(r.started_at).toLocaleString('ro-RO')}</span>
                    </div>
                    {r.output && <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', background: 'var(--bg-primary)', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>{r.output}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Închidere</button></div>
      </div>
    </div>
  );
}

export default function CronJobs() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [modal, setModal] = useState(null);
  const [runsJob, setRunsJob] = useState(null);
  const [running, setRunning] = useState({});
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, s] = await Promise.all([
        api.get('/cron/jobs', { params: { status: filterStatus || undefined } }),
        api.get('/cron/stats'),
      ]);
      setList(j.data); setStats(s.data);
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const runNow = async (id, name) => {
    setRunning(p => ({ ...p, [id]: true }));
    try {
      await api.post(`/cron/jobs/${id}/run`);
      toast.success(`"${name}" pornit`);
      setTimeout(() => { setRunning(p => ({ ...p, [id]: false })); load(); }, 2000);
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); setRunning(p => ({ ...p, [id]: false })); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Ștergi job-ul "${name}"?`)) return;
    try { await api.delete(`/cron/jobs/${id}`); toast.success('Job șters'); load(); }
    catch (e) { toast.error('Eroare'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Cron Jobs</div><div className="page-subtitle">Programare și automatizare task-uri</div></div>
        <button className="btn btn-primary" onClick={() => setModal({ new: true })}><Plus size={14} /> Job Nou</button>
      </div>

      {stats && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Total Job-uri', value: stats.total, color: 'var(--accent)', icon: Clock },
            { label: 'Active', value: stats.active, color: 'var(--success)', icon: CheckCircle },
            { label: 'Pauză', value: stats.paused, color: 'var(--warning)', icon: Pause },
            { label: 'Eșuate (ultima)', value: stats.last_failed, color: 'var(--danger)', icon: AlertTriangle },
          ].map(k => (
            <div key={k.label} className="stat-card">
              <div className="stat-icon" style={{ background: `${k.color}20`, marginBottom: 10 }}><k.icon size={16} color={k.color} /></div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, justifyContent: 'flex-end' }}>
          <select className="form-select" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Toate statusurile</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
        </div>

        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>NUME</th><th>SCHEDULE</th><th>COMANDĂ</th><th>STATUS</th><th>ULTIMA RULARE</th><th>REZULTAT</th><th>DURATĂ</th><th>ACȚIUNI</th></tr>
              </thead>
              <tbody>
                {list.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Niciun cron job</td></tr>}
                {list.map(j => {
                  const sCfg = STATUS_CFG[j.status] || STATUS_CFG.active;
                  const lsCfg = j.last_status ? LAST_STATUS_CFG[j.last_status] : null;
                  return (
                    <tr key={j.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{j.name}</div>
                        {j.description && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{j.description}</div>}
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 6, background: 'var(--bg-hover)', color: 'var(--text-secondary)' }}>{j.type}</span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{j.schedule}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-secondary)' }}>{j.command}</td>
                      <td><span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, color: sCfg.color, background: sCfg.bg }}>{sCfg.label}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{j.last_run ? new Date(j.last_run).toLocaleString('ro-RO') : '—'}</td>
                      <td>
                        {lsCfg ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <lsCfg.icon size={13} color={lsCfg.color} />
                            <span style={{ fontSize: 11, color: lsCfg.color }}>{lsCfg.label}</span>
                          </div>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{j.last_duration ? `${j.last_duration}s` : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-icon" title="Rulare manuală" style={{ color: 'var(--success)' }} onClick={() => runNow(j.id, j.name)} disabled={running[j.id]}>
                            {running[j.id] ? <span className="spinner" style={{ width: 13, height: 13 }} /> : <Play size={13} />}
                          </button>
                          <button className="btn-icon" title="Istoricul rulărilor" onClick={() => setRunsJob(j)}><FileText size={13} /></button>
                          <button className="btn-icon" title="Editare" onClick={() => setModal(j)}><Edit2 size={13} /></button>
                          {hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(j.id, j.name)}><Trash2 size={13} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <JobModal job={modal.new ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {runsJob && <RunsModal job={runsJob} onClose={() => setRunsJob(null)} />}
    </div>
  );
}
